import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import moment from 'moment-timezone';
import { Router } from '@angular/router';

@Component({
  selector: 'app-order-description',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './order-description.component.html',
  styleUrl: './order-description.component.css',
})
export class OrderDescriptionComponent {
  readonly dialogRef = inject(MatDialogRef<OrderDescriptionComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  selectedOrder: any;
  groupByCustomerList: { [key: number]: any[] } = {};
  imageErrors: Set<string | number> = new Set();
  restaurantTimezone: any;
  router = inject(Router);
  ngOnInit() {
    this.selectedOrder = this.data.selectedOrder;
    this.setOrdersDisplayStatus([this.selectedOrder]);
    this.groupByCustomerList = this.groupByCustomer(this.selectedOrder.menu_items);
    this.restaurantTimezone = this.data?.restaurantTimezone;
  }

  setOrdersDisplayStatus(orders: any[]) {
    if (orders && orders.length > 0) {
      orders.forEach((order: any) => {
        if (order.status == 'ready_to_order') {
          order.displayStatus = 'Ready to order';
        } else if (order.status == 'in_progress') {
          order.displayStatus = 'In progress';
        } else if (order.status == 'ready_for_payment') {
          order.displayStatus = 'Ready for payment';
        } else if (order.status == 'payment_option_selected') {
          order.displayStatus = 'Payment selected';
        } else if (order.status == 'payment_completed') {
          order.displayStatus = 'Payment completed';
        } else if (order.status == 'completed') {
          order.displayStatus = 'Completed';
        } else if (order.status == 'cancelled') {
          order.displayStatus = 'Cancelled';
        }
      });
    }
  }

  getCustomerName(id: any) {
    let findCustomer = this.selectedOrder.customers.find((f: any) => f.user_id == id);
    return findCustomer?.first_name ?? '';
  }

  groupByCustomer(array: any[]) {
    return array.reduce(
      (result, currentCustomer) => {
        if (!result[currentCustomer?.variation?.ordered_by]) {
          result[currentCustomer?.variation?.ordered_by] = [];
        }
        result[currentCustomer?.variation?.ordered_by].push(currentCustomer);
        return result;
      },
      {} as Record<number, any[]>,
    );
  }

  getFirstLetterOfString(str: string) {
    if (str && str.length > 0) {
      return str.charAt(0).toUpperCase();
    }
    return '';
  }

  // Customer photo when one is set and it loads; otherwise the initial badge.
  private failedCustomerImages = new Set<string>();

  hasCustomerImage(customer: any): boolean {
    const url = customer?.img_path?.trim();
    return !!url && !this.failedCustomerImages.has(url);
  }

  onCustomerImageError(customer: any): void {
    this.failedCustomerImages.add(customer.img_path.trim());
  }

  truncateString(input: string, maxLength: number) {
    return input.length > maxLength ? input.slice(0, maxLength) + ' ... ' : input;
  }

  closeDescription() {
    this.dialogRef.close();
  }

  getRgbaColor(hexColor: string | undefined, opacity: number): string {
    if (!hexColor) return 'rgba(0, 0, 0, 0)'; // Default to transparent if color is undefined
    // Convert hex to RGB
    let r: number, g: number, b: number;

    if (hexColor.startsWith('#')) {
      const bigint = parseInt(hexColor.slice(1), 16);
      r = (bigint >> 16) & 255;
      g = (bigint >> 8) & 255;
      b = bigint & 255;
    } else if (hexColor.startsWith('rgb')) {
      // If the color is already in RGB format
      const rgbValues = hexColor.match(/\d+/g);
      if (!rgbValues) return 'rgba(0, 0, 0, 0)';
      [r, g, b] = rgbValues.map(Number);
    } else {
      return hexColor; // Fallback to original color if format is unsupported
    }

    // Return RGBA color with the desired opacity
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  onMenuThumbnailImageError(event: Event, itemId: string | number): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/empty-menu.png';
    this.imageErrors.add(itemId);
  }

  hasImageError(itemId: string | number): boolean {
    return this.imageErrors.has(itemId);
  }

  onServedByImageError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/common/user-circle.svg';
  }

  displayCreateddate(created_at: string) {
    if (created_at && this.restaurantTimezone) {
      return moment(created_at).tz(this.restaurantTimezone)?.format('DD MMM, YYYY HH:mm') || '';
    } else if (created_at) {
      return moment(created_at).format('DD MMM, YYYY HH:mm') || '';
    } else {
      return 'N/A';
    }
  }

  openPaymentSummary() {
    if (!this.selectedOrder || !this.selectedOrder.order_id) {
      console.error('Order ID is missing');
      return;
    }
    this.dialogRef.close();
    this.router.navigate(['/main/order-payment', this.selectedOrder.order_id]);
  }

  /// The guest feedback left on this order — read-only, and a different
  /// system from the restaurant's public reviews.
  openFeedback() {
    if (!this.selectedOrder || !this.selectedOrder.order_id) {
      console.error('Order ID is missing');
      return;
    }
    this.dialogRef.close();
    this.router.navigate(['/main/order-feedback', this.selectedOrder.order_id]);
  }

  getOrderTableLabel(): string {
    if (
      this.selectedOrder?.order_type === 'togo' ||
      this.selectedOrder?.order_type === 'togo_dine_in'
    ) {
      return 'ToGo';
    }
    if (this.selectedOrder?.online_platform_type && this.selectedOrder?.order_type === 'online') {
      let platformType =
        this.selectedOrder.online_platform_type.toLowerCase() == 'forknfind'
          ? 'F&F'
          : this.selectedOrder.online_platform_type;
      return platformType;
    }
    if (this.selectedOrder?.tables?.length > 0) {
      return this.selectedOrder.tables[0].table_name;
    }
    return 'N/A';
  }

  getStatusVariant(): 'in-progress' | 'placed' | 'completed' | 'cancelled' {
    if (this.selectedOrder?.status === 'order_placed') {
      return 'placed';
    }
    if (this.selectedOrder?.status === 'completed') {
      return 'completed';
    }
    if (this.selectedOrder?.status === 'cancelled') {
      return 'cancelled';
    }
    return 'in-progress';
  }
}
